<template>
  <div class="panel panel-default">
    <div class="panel-body">
      <h4>{{ formMode === SCHEDULE_FORM_MODE.EDIT ? 'Edit' : 'Create' }} Daily Provisioning Schedule</h4>
      <div v-if="loading" class="alert alert-info">
        <p>Processing...</p>
      </div>
      <div v-if="error" class="alert alert-danger">
        <p>{{ error }}</p>
      </div>
      <form @submit.prevent="saveSchedule">
        <div class="form-group">
          <label class="control-label">Timezone</label>
          <select v-model="schedule.timezone" class="form-control" required>
            <option value="America/New_York">Eastern</option>
            <option value="America/Chicago">Central</option>
            <option value="America/Denver">Mountain</option>
            <option value="America/Los_Angeles">Pacific</option>
            <option value="America/Honolulu">Hawaii</option>
            <option value="America/Phoenix">Arizona</option>
          </select>
        </div>

        <div class="form-group">
          <label class="control-label">Time</label>
          <div class="time-input">
            <select v-model="schedule.hour" class="form-control time-select" required>
              <option v-for="h in 12" :key="`hour-${h}`" :value="h">{{ h }}</option>
            </select>
            <span class="time-separator">:</span>
            <select v-model="schedule.minute" class="form-control time-select" required>
              <option v-for="m in 12" :key="`minute-${(m-1)*5}`" :value="(m-1)*5">{{ ((m-1)*5).toString().padStart(2, '0') }}</option>
            </select>
            <select v-model="schedule.ampm" class="form-control time-select" required>
              <option value="am">AM</option>
              <option value="pm">PM</option>
            </select>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn btn-primary" :disabled="loading">{{ loading ? 'Saving...' : 'Save' }}</button>
          <button type="button" class="btn btn-default" @click="cancel">Cancel</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { createCronExpression, parseCronExpression, convert12to24, convert24to12, formatTime } from '../../utils/formatters'

export default {
  name: 'AutoprovisioningScheduleForm',
  props: {
    domainUuid: {
      type: String,
      required: true
    },
    domainName: {
      type: String,
      required: true
    },
    existingSchedule: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      SCHEDULE_FORM_MODE: {
        CREATE: 'create',
        EDIT: 'edit'
      },
      schedule: {
        hour: 11,
        minute: 0,
        ampm: 'pm',
        timezone: 'America/Los_Angeles'
      }
    }
  },
  computed: {
    formMode() {
      return this.existingSchedule ? this.SCHEDULE_FORM_MODE.EDIT : this.SCHEDULE_FORM_MODE.CREATE
    },
    isEdit() {
      return this.formMode === this.SCHEDULE_FORM_MODE.EDIT
    },
    formattedTime() {
      return this.getFormattedTime(this.schedule.hour, this.schedule.minute, this.schedule.ampm)
    }
  },
  methods: {
    saveSchedule() {
      this.loading = true
      this.error = null

      const hour24 = convert12to24(this.schedule.hour, this.schedule.ampm)
      const cronExpression = createCronExpression(hour24, this.schedule.minute)

      const scheduleData = {
        domain_name: this.domainName,
        cron_expression: cronExpression,
        timezone: this.schedule.timezone
      }

      console.log('Saving schedule:', scheduleData)
      const apiMethod = this.formMode === this.SCHEDULE_FORM_MODE.EDIT ? 'updateAutoprovisioningSchedule' : 'createAutoprovisioningSchedule'
      this.$angularWorld.adminApi[apiMethod](this.domainUuid, scheduleData)
        .then(schedule => {
          this.loading = false
          if (this.formMode === this.SCHEDULE_FORM_MODE.EDIT) {
            this.$emit('scheduleUpdated', schedule)
          } else {
            this.$emit('scheduleCreated', schedule)
          }
        })
        .catch(error => {
          console.error('Error saving schedule:', error)
          this.loading = false
          this.error = error.message || 'An error occurred while saving the schedule'
        })
    },
    cancel() {
      this.$emit('cancel')
    },
    initializeFromExistingSchedule() {
      if (this.existingSchedule) {
        let hour = 11
        let minute = 0
        let ampm = 'pm'
        
        const parsedCron = parseCronExpression(this.existingSchedule.ScheduleExpression)
        if (parsedCron) {
          const timeObj = convert24to12(parsedCron.hour, parsedCron.minute)
          hour = timeObj.hour
          minute = timeObj.minute
          ampm = timeObj.ampm
        }
        
        this.schedule = {
          hour: hour,
          minute: minute,
          ampm: ampm,
          timezone: this.existingSchedule.ScheduleExpressionTimezone || 'America/Los_Angeles'
        }
      }
    },
    getFormattedTime(hour, minute, ampm) {
      return formatTime(hour, minute, ampm)
    },
    get24HourValue() {
      return convert12to24(this.schedule.hour, this.schedule.ampm)
    }
  },
  created() {
    this.initializeFromExistingSchedule()
  }
}
</script>

<style scoped>
.panel {
  margin-top: 20px;
}
.form-group {
  margin-bottom: 20px;
}
.time-input {
  display: flex;
  align-items: center;
}
.time-select {
  width: 80px;
}
select {
  max-width: 150px;
}
.time-separator {
  display: inline-block;
  font-size: 20px;
  line-height: 34px;
  font-weight: bold;
  margin: 0 10px;
}
.form-actions {
  margin-top: 30px;
}
</style>
